import { useState, useEffect, useRef } from "react";
import SimpleBanner from "./SimpleBanner";

// ── Data ──────────────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: "introduction",
    label: "Introduction",
    title: "Introduction",
    accent: "#0ea5e9",
    content: [
      {
        type: "paragraph",
        text: "Welcome to Kgs Technology. By accessing our website and services, you agree to comply with the terms outlined in this policy. This document governs our professional conduct, data handling, and service commitments.",
      },
    ],
  },
  {
    id: "privacy",
    label: "Privacy Policy",
    title: "Privacy Policy",
    accent: "#0ea5e9",
    content: [
      {
        type: "subsection",
        heading: "1. Privacy Policy",
        text: "We are committed to protecting your privacy. Any personal information collected is used solely for business purposes, including client communication, recruitment, and service improvements. We do not sell or share personal data with third parties except as required by law.",
      },
      {
        type: "subsection",
        heading: "2. Data Security",
        text: "We takes data security seriously. We implement industry-standard measures to safeguard information from unauthorized access, alteration, or disclosure. However, while we strive to protect your data, we cannot guarantee complete security.",
      },
    ],
  },
  {
    id: "terms",
    label: "Terms & Conditions",
    title: "Terms & Conditions",
    accent: "#0ea5e9",
    content: [
      {
        type: "subsection",
        heading: "1. Compliance and Ethics",
        text: "We adhere to all applicable laws and ethical standards in staffing and IT solutions. Our commitment to integrity, diversity, and inclusion ensures fairness in all business interactions.",
      },
      {
        type: "subsection",
        heading: "2. Service Terms",
        text: "Our services include IT staffing, workforce solutions, and consulting. We strive for excellence in delivery but do not provide warranties for uninterrupted services. Any reliance on our services is at your own discretion.",
      },
      {
        type: "subsection",
        heading: "3. Intellectual Property",
        text: "All content on our website, including text, logos, and images, is the property of Kgs technology Group. Unauthorized use or reproduction is strictly prohibited.",
      },
      {
        type: "subsection",
        heading: "4. Third-Party Links",
        text: "Our website may contain links to external sites. We are not responsible for the content, policies, or practices of third-party websites.",
      },
      {
        type: "subsection",
        heading: "5. Limitation of Liability",
        text: "We are not liable for any direct, indirect, incidental, or consequential damages resulting from the use of our services or website.",
      },
    ],
  },
  {
    id: "other",
    label: "Other Provisions",
    title: "Other Provisions",
    accent: "#0ea5e9",
    content: [
      {
        type: "subsection",
        heading: "1. Updates to Policy",
        text: "This policy may be updated periodically. Continued use of our services after changes indicates acceptance of the revised terms.",
      },
      {
        type: "subsection",
        heading: "2. Contact Information",
        text: "For any inquiries or concerns regarding this policy, please contact us at",
        link: { label: "info@kgstechnologygroup.com", href: "mailto:contact@voltoconsulting.com" },
      },
    ],
  },
];

// ── Animated Section ──────────────────────────────────────────────────────────
function AnimatedSection({ children, delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ── Content Renderer ──────────────────────────────────────────────────────────
function SectionContent({ content }) {
  return (
    <div className="space-y-8">
      {content.map((block, i) => {
        if (block.type === "paragraph") {
          return (
            <AnimatedSection key={i} delay={100}>
              <p className="text-gray-600 leading-relaxed text-base">{block.text}</p>
            </AnimatedSection>
          );
        }
        if (block.type === "subsection") {
          return (
            <AnimatedSection key={i} delay={i * 80 + 100}>
              <div className="group">
                <div className="flex items-start gap-3">
                  <div
                    className="mt-1 w-1 flex-shrink-0 rounded-full"
                    style={{ height: "1.25rem", background: "linear-gradient(180deg, #0ea5e9, #38bdf8)" }}
                  />
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2 text-base tracking-wide">
                      {block.heading}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {block.text}
                      {block.link && (
                        <>
                          {" "}
                          <a
                            href={block.link.href}
                            className="text-sky-500 hover:text-sky-600 underline underline-offset-2 transition-colors"
                          >
                            {block.link.label}
                          </a>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          );
        }
        return null;
      })}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TermsAndPrivacy() {
  const [activeSection, setActiveSection] = useState("introduction");
  const sectionRefs = useRef({});

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      let current = SECTIONS[0].id;
      for (const section of SECTIONS) {
        const el = sectionRefs.current[section.id];
        if (el && el.getBoundingClientRect().top <= 120) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-gray-50 " style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
        <SimpleBanner title='Privacy Policy and Terms & Conditions' />
    

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex gap-10">

        {/* Sidebar Nav */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-24">
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4 px-3" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
              Contents
            </p>
            <nav className="space-y-1">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200"
                  style={{
                    fontFamily: "'Segoe UI', sans-serif",
                    background: activeSection === s.id ? "linear-gradient(135deg, #e0f2fe, #f0f9ff)" : "transparent",
                    color: activeSection === s.id ? "#0284c7" : "#6b7280",
                    fontWeight: activeSection === s.id ? "600" : "400",
                    borderLeft: activeSection === s.id ? "3px solid #0ea5e9" : "3px solid transparent",
                  }}
                >
                  {s.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="space-y-16">
            {SECTIONS.map((section, sIdx) => (
              <div
                key={section.id}
                id={section.id}
                ref={(el) => (sectionRefs.current[section.id] = el)}
                className="scroll-mt-24"
              >
                <AnimatedSection delay={0}>
                  {/* Section Header */}
                  <div className="mb-8">
                    <div className="flex items-center gap-4 mb-3">
                      {sIdx > 0 && (
                        <div className="h-px flex-1 bg-gradient-to-r from-sky-200 to-transparent" />
                      )}
                    </div>
                    <h2
                      className="text-3xl font-bold text-gray-900 tracking-tight"
                      style={{ fontFamily: "'Georgia', serif" }}
                    >
                      {section.title}
                    </h2>
                    <div
                      className="mt-2 h-0.5 w-12 rounded-full"
                      style={{ background: "linear-gradient(90deg, #0ea5e9, #38bdf8)" }}
                    />
                  </div>

                  <SectionContent content={section.content} />
                </AnimatedSection>
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div className="mt-20 pt-8 border-t border-gray-200">
            <p className="text-xs text-gray-400 text-center" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
              © {new Date().getFullYear()} Kgs Technology Group. All rights reserved. This document is subject to change without prior notice.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}